/**
 * Portal logado — visão do CLIENTE e do TÉCNICO sobre seus próprios dados.
 *
 * Role decide qual painel renderiza. Dados vêm escopados do backend (/v1/portal/*),
 * então o usuário só enxerga o que é dele.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import {
  AlertCircle,
  Briefcase,
  CalendarClock,
  CheckCircle2,
  FileText,
  Loader2,
  Receipt,
  Upload,
  Wallet,
} from "lucide-react";
import { useState, type ChangeEvent, type ReactNode } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";

import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/ui/data-table";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  CLOSING_STATUS_LABEL,
  CLOSING_STATUS_VARIANT,
  INVOICE_STATUS_LABEL,
  INVOICE_STATUS_VARIANT,
  JOB_STATUS_LABEL,
  JOB_STATUS_VARIANT,
  MONTHS_PT,
  portalApi,
  type PortalClosing,
  type PortalClosingDetail,
  type PortalInvoice,
  type PortalJob,
} from "@/lib/api-portal";
import { formatCurrency, formatDate } from "@/lib/format";
import { useAuth } from "@/stores/auth";

function Stat({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: typeof Briefcase;
  label: string;
  value: ReactNode;
  hint?: string;
}) {
  return (
    <div className="rounded-2xl border border-slate-200/60 bg-white/70 p-4 dark:border-white/10 dark:bg-card/40">
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className="h-4 w-4" />
        <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <div className="mt-2 text-2xl font-bold tabular-nums">{value}</div>
      {hint && <div className="mt-0.5 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

function JobStatusBadge({ status }: { status: string }) {
  return (
    <Badge variant={(JOB_STATUS_VARIANT[status] ?? "muted") as never} dot>
      {JOB_STATUS_LABEL[status] ?? status}
    </Badge>
  );
}

function NotLinked({ kind }: { kind: "cliente" | "técnico" }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 p-8 text-center dark:border-white/10">
      <AlertCircle className="mx-auto h-8 w-8 text-amber-500" />
      <p className="mt-2 font-medium">Conta ainda não vinculada a um {kind}</p>
      <p className="mt-1 text-sm text-muted-foreground">
        Sua conta está sem vínculo de {kind} na base. Fale com o suporte da EuNerd para liberar o acesso.
      </p>
    </div>
  );
}

const jobColumns = (forNerd: boolean): ColumnDef<PortalJob>[] => [
  {
    id: "code",
    header: "Código",
    cell: ({ row }) => <span className="font-mono text-xs">{row.original.code}</span>,
  },
  {
    id: "title",
    header: "Chamado",
    cell: ({ row }) => (
      <div className="min-w-0">
        <div className="truncate font-medium">{row.original.title}</div>
        <div className="truncate text-xs text-muted-foreground">
          {row.original.category_name ?? "—"}
          {row.original.city ? ` · ${row.original.city}` : ""}
        </div>
      </div>
    ),
  },
  {
    id: "status",
    header: "Status",
    cell: ({ row }) => <JobStatusBadge status={row.original.status} />,
  },
  {
    id: "date",
    header: "Data",
    cell: ({ row }) => (
      <span className="text-sm">
        {formatDate(row.original.completed_at ?? row.original.scheduled_at)}
      </span>
    ),
  },
  {
    id: "value",
    header: forNerd ? "A receber" : "Valor",
    cell: ({ row }) => (
      <span className="tabular-nums">
        {formatCurrency(forNerd ? row.original.nerd_payout : row.original.price)}
      </span>
    ),
  },
];

// ─── Cliente ──────────────────────────────────────────────────────────────
function ClientClosingReview({
  closing,
  onRefresh,
  onConfirmed,
}: {
  closing: PortalClosingDetail;
  onRefresh: () => void;
  onConfirmed: () => void;
}) {
  const editable = closing.status === "sent" || closing.status === "under_review";
  const [disputes, setDisputes] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      closing.items.filter((i) => !i.recognized).map((i) => [i.id, i.dispute_reason ?? ""]),
    ),
  );

  const toggle = (id: string) =>
    setDisputes((prev) => {
      const next = { ...prev };
      if (id in next) delete next[id];
      else next[id] = "";
      return next;
    });

  const recognizedTotal = closing.items
    .filter((i) => !(i.id in disputes))
    .reduce((sum, i) => sum + parseFloat(i.amount), 0);
  const disputedTotal = closing.items
    .filter((i) => i.id in disputes)
    .reduce((sum, i) => sum + parseFloat(i.amount), 0);

  const confirm = useMutation({
    mutationFn: async () => {
      const disputed = Object.entries(disputes).map(([item_id, reason]) => ({
        item_id,
        reason: reason.trim() || null,
      }));
      await portalApi.clientReviewClosing(closing.id, disputed);
      await portalApi.clientConfirmClosing(closing.id);
    },
    onSuccess: () => {
      toast.success("Fechamento confirmado! A EuNerd vai conciliar e faturar.");
      onRefresh();
      onConfirmed();
    },
    onError: () => toast.error("Falha ao confirmar o fechamento"),
  });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-2 text-sm">
        <div className="rounded-lg border p-2">
          <div className="text-xs text-muted-foreground">Bruto</div>
          <div className="font-semibold tabular-nums">{formatCurrency(closing.gross_total)}</div>
        </div>
        <div className="rounded-lg border p-2">
          <div className="text-xs text-muted-foreground">A faturar</div>
          <div className="font-semibold tabular-nums text-emerald-600">
            {formatCurrency(editable ? recognizedTotal : closing.recognized_total)}
          </div>
        </div>
        <div className="rounded-lg border p-2">
          <div className="text-xs text-muted-foreground">Contestado</div>
          <div className="font-semibold tabular-nums text-rose-600">
            {formatCurrency(editable ? disputedTotal : closing.disputed_total)}
          </div>
        </div>
      </div>

      {editable && (
        <p className="text-xs text-muted-foreground">
          Desmarque os atendimentos que você <strong>não reconhece</strong> e confirme para fechar o
          mês.
        </p>
      )}

      <div className="space-y-1">
        {closing.items.map((it) => {
          const disputed = it.id in disputes;
          return (
            <div
              key={it.id}
              className={`rounded-lg border p-2 text-sm ${
                disputed ? "border-rose-200 bg-rose-50 dark:bg-rose-500/5" : ""
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <label className="flex min-w-0 items-center gap-2">
                  {editable && (
                    <input
                      type="checkbox"
                      className="h-4 w-4 shrink-0 accent-primary-600"
                      checked={!disputed}
                      onChange={() => toggle(it.id)}
                    />
                  )}
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{it.description}</span>
                    <span className="block text-xs text-muted-foreground">
                      {it.job_code}
                      {it.scheduled_at && ` · ${formatDate(it.scheduled_at)}`}
                      {!editable && disputed && " · não reconhecido"}
                    </span>
                  </span>
                </label>
                <span className="tabular-nums whitespace-nowrap">{formatCurrency(it.amount)}</span>
              </div>
              {editable && disputed && (
                <input
                  className="mt-2 w-full rounded-md border border-rose-200 bg-white px-2 py-1 text-xs dark:bg-card dark:border-rose-500/30"
                  placeholder="Motivo (opcional)"
                  value={disputes[it.id]}
                  onChange={(e) => setDisputes((prev) => ({ ...prev, [it.id]: e.target.value }))}
                />
              )}
            </div>
          );
        })}
      </div>

      {editable ? (
        <Button className="w-full" onClick={() => confirm.mutate()} disabled={confirm.isPending}>
          {confirm.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <CheckCircle2 className="h-4 w-4" />
          )}
          Confirmar fechamento do mês
        </Button>
      ) : (
        <p className="text-center text-xs text-muted-foreground">
          {closing.status === "finalized"
            ? "Fechamento finalizado pela EuNerd."
            : "Fechamento confirmado. Aguardando conciliação da EuNerd."}
        </p>
      )}
    </div>
  );
}

function ClientPortal() {
  const [openId, setOpenId] = useState<string | null>(null);
  const qc = useQueryClient();
  const summary = useQuery({ queryKey: ["portal-client-summary"], queryFn: portalApi.clientSummary });
  const jobs = useQuery({
    queryKey: ["portal-client-jobs"],
    queryFn: () => portalApi.clientJobs({ size: 100 }),
  });
  const closings = useQuery({
    queryKey: ["portal-client-closings"],
    queryFn: () => portalApi.clientClosings({ size: 100 }),
  });
  const detail = useQuery({
    queryKey: ["portal-client-closing", openId],
    queryFn: () => portalApi.clientClosing(openId!),
    enabled: !!openId,
  });

  if (summary.isError) return <NotLinked kind="cliente" />;
  const s = summary.data;
  const d = detail.data;

  const closingColumns: ColumnDef<PortalClosing>[] = [
    {
      id: "period",
      header: "Período",
      cell: ({ row }) => (
        <span className="font-medium">
          {MONTHS_PT[row.original.month]}/{row.original.year}
        </span>
      ),
    },
    {
      id: "status",
      header: "Status",
      cell: ({ row }) => (
        <Badge variant={(CLOSING_STATUS_VARIANT[row.original.status] ?? "muted") as never} dot>
          {CLOSING_STATUS_LABEL[row.original.status] ?? row.original.status}
        </Badge>
      ),
    },
    {
      id: "items",
      header: "Atend.",
      cell: ({ row }) => <span className="tabular-nums">{row.original.item_count}</span>,
    },
    {
      id: "gross",
      header: "Bruto",
      cell: ({ row }) => <span className="tabular-nums">{formatCurrency(row.original.gross_total)}</span>,
    },
    {
      id: "recognized",
      header: "A faturar",
      cell: ({ row }) => (
        <span className="tabular-nums text-emerald-600">
          {formatCurrency(row.original.recognized_total)}
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader title="Meu painel" description="Seus chamados e o faturamento do mês" />
      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat icon={Briefcase} label="Chamados abertos" value={s?.open_jobs ?? "—"} />
        <Stat icon={CheckCircle2} label="Concluídos no mês" value={s?.completed_this_month ?? "—"} />
        <Stat
          icon={Wallet}
          label="A faturar no mês"
          value={s ? formatCurrency(s.billing_estimate_month) : "—"}
          hint="estimativa dos concluídos"
        />
        <Stat icon={Receipt} label="Apurações p/ conferir" value={s?.pending_closings ?? "—"} />
      </div>

      <Tabs defaultValue="apuracoes">
        <TabsList>
          <TabsTrigger value="apuracoes">Apurações</TabsTrigger>
          <TabsTrigger value="chamados">Chamados</TabsTrigger>
        </TabsList>
        <TabsContent value="apuracoes">
          <DataTable
            columns={closingColumns}
            data={closings.data?.items ?? []}
            loading={closings.isLoading}
            onRowClick={(row) => setOpenId(row.id)}
          />
        </TabsContent>
        <TabsContent value="chamados">
          <DataTable
            columns={jobColumns(false)}
            data={jobs.data?.items ?? []}
            loading={jobs.isLoading}
          />
        </TabsContent>
      </Tabs>

      <Sheet open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <SheetContent side="right" size="lg">
          <SheetHeader>
            <SheetTitle>
              {d ? `Apuração ${MONTHS_PT[d.month]}/${d.year}` : "Apuração"}
            </SheetTitle>
          </SheetHeader>
          <SheetBody>
            {detail.isLoading || !d ? (
              <p className="text-sm text-muted-foreground">Carregando…</p>
            ) : (
              <ClientClosingReview
                key={d.id}
                closing={d}
                onRefresh={() => {
                  qc.invalidateQueries({ queryKey: ["portal-client-closing", openId] });
                  qc.invalidateQueries({ queryKey: ["portal-client-closings"] });
                  qc.invalidateQueries({ queryKey: ["portal-client-summary"] });
                }}
                onConfirmed={() => setOpenId(null)}
              />
            )}
          </SheetBody>
        </SheetContent>
      </Sheet>
    </>
  );
}

// ─── Técnico ──────────────────────────────────────────────────────────────
function NfUpload({ invoiceId, onDone }: { invoiceId: string; onDone: () => void }) {
  const [uploading, setUploading] = useState(false);

  const handle = async (file: File) => {
    setUploading(true);
    try {
      const ct = file.type || "application/pdf";
      const { upload_url, storage_path } = await portalApi.nerdNfUploadUrl(
        invoiceId,
        file.name,
        ct,
      );
      if (!upload_url.startsWith("https://localhost")) {
        const r = await fetch(upload_url, {
          method: "PUT",
          headers: { "Content-Type": ct },
          body: file,
        });
        if (!r.ok) throw new Error(`upload ${r.status}`);
      }
      await portalApi.nerdSubmitNf(invoiceId, file.name, storage_path);
      toast.success("NF enviada! Vamos analisar e programar o pagamento.");
      onDone();
    } catch (e) {
      toast.error("Falha ao enviar NF", {
        description: e instanceof Error ? e.message : undefined,
      });
    } finally {
      setUploading(false);
    }
  };

  const onInput = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) void handle(f);
    e.target.value = "";
  };

  return (
    <div className="rounded-xl border border-dashed border-primary-300 bg-primary-50/40 p-4 dark:border-primary-500/30 dark:bg-primary-500/5">
      <p className="mb-2 text-sm font-medium">Enviar nota fiscal</p>
      <input id={`nf-${invoiceId}`} type="file" accept="application/pdf,image/*" className="hidden" onChange={onInput} />
      <Button
        size="sm"
        onClick={() => document.getElementById(`nf-${invoiceId}`)?.click()}
        disabled={uploading}
      >
        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
        {uploading ? "Enviando…" : "Escolher arquivo (PDF/imagem)"}
      </Button>
    </div>
  );
}

function NerdPortal() {
  const [openId, setOpenId] = useState<string | null>(null);
  const qc = useQueryClient();
  const summary = useQuery({ queryKey: ["portal-nerd-summary"], queryFn: portalApi.nerdSummary });
  const jobs = useQuery({
    queryKey: ["portal-nerd-jobs"],
    queryFn: () => portalApi.nerdJobs({ size: 100 }),
  });
  const invoices = useQuery({
    queryKey: ["portal-nerd-invoices"],
    queryFn: () => portalApi.nerdInvoices({ size: 100 }),
  });
  const detail = useQuery({
    queryKey: ["portal-nerd-invoice", openId],
    queryFn: () => portalApi.nerdInvoice(openId!),
    enabled: !!openId,
  });

  if (summary.isError) return <NotLinked kind="técnico" />;
  const s = summary.data;
  const d = detail.data;

  const invoiceColumns: ColumnDef<PortalInvoice>[] = [
    {
      id: "code",
      header: "Fatura",
      cell: ({ row }) => <span className="font-mono text-xs">{row.original.code}</span>,
    },
    {
      id: "period",
      header: "Período",
      cell: ({ row }) =>
        row.original.period_month ? (
          <span>
            {MONTHS_PT[row.original.period_month]}/{row.original.period_year}
          </span>
        ) : (
          <span className="text-slate-400">—</span>
        ),
    },
    {
      id: "status",
      header: "Status",
      cell: ({ row }) => (
        <Badge variant={(INVOICE_STATUS_VARIANT[row.original.status] ?? "muted") as never} dot>
          {INVOICE_STATUS_LABEL[row.original.status] ?? row.original.status}
        </Badge>
      ),
    },
    {
      id: "total",
      header: "Total",
      cell: ({ row }) => <span className="tabular-nums font-medium">{formatCurrency(row.original.total)}</span>,
    },
    {
      id: "payment",
      header: "Pagamento",
      cell: ({ row }) =>
        row.original.scheduled_payment_date ? (
          <span className="text-sm">{formatDate(row.original.scheduled_payment_date)}</span>
        ) : (
          <span className="text-slate-400">a programar</span>
        ),
    },
  ];

  return (
    <>
      <PageHeader title="Meu painel" description="Seus atendimentos e o que você tem a receber" />
      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat icon={Briefcase} label="Atend. em aberto" value={s?.assigned_open ?? "—"} />
        <Stat icon={CheckCircle2} label="Concluídos no mês" value={s?.completed_this_month ?? "—"} />
        <Stat
          icon={Wallet}
          label="A receber"
          value={s ? formatCurrency(s.to_receive) : "—"}
          hint={`${s?.open_invoices ?? 0} fatura(s) em aberto`}
        />
        <Stat
          icon={CalendarClock}
          label="Próx. pagamento"
          value={s?.next_payment_date ? formatDate(s.next_payment_date) : "—"}
        />
      </div>

      <Tabs defaultValue="faturas">
        <TabsList>
          <TabsTrigger value="faturas">Minhas faturas</TabsTrigger>
          <TabsTrigger value="servicos">Meus serviços</TabsTrigger>
        </TabsList>
        <TabsContent value="faturas">
          <DataTable
            columns={invoiceColumns}
            data={invoices.data?.items ?? []}
            loading={invoices.isLoading}
            onRowClick={(row) => setOpenId(row.id)}
          />
        </TabsContent>
        <TabsContent value="servicos">
          <DataTable
            columns={jobColumns(true)}
            data={jobs.data?.items ?? []}
            loading={jobs.isLoading}
          />
        </TabsContent>
      </Tabs>

      <Sheet open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <SheetContent side="right" size="lg">
          <SheetHeader>
            <SheetTitle>{d ? `Fatura ${d.code}` : "Fatura"}</SheetTitle>
          </SheetHeader>
          <SheetBody>
            {detail.isLoading || !d ? (
              <p className="text-sm text-muted-foreground">Carregando…</p>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={(INVOICE_STATUS_VARIANT[d.status] ?? "muted") as never} dot>
                    {INVOICE_STATUS_LABEL[d.status] ?? d.status}
                  </Badge>
                  {d.nf_deadline && (
                    <span className="text-xs text-muted-foreground">
                      NF até {formatDate(d.nf_deadline)}
                    </span>
                  )}
                  {d.nf_submitted_at && (
                    <Badge variant={"success" as never}>
                      <FileText className="h-3 w-3" /> NF enviada
                    </Badge>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="rounded-lg border p-2">
                    <div className="text-xs text-muted-foreground">Serviços</div>
                    <div className="font-semibold tabular-nums">{formatCurrency(d.services_total)}</div>
                  </div>
                  <div className="rounded-lg border p-2">
                    <div className="text-xs text-muted-foreground">Total</div>
                    <div className="font-semibold tabular-nums text-emerald-600">
                      {formatCurrency(d.total)}
                    </div>
                  </div>
                </div>
                <div className="space-y-1">
                  {d.items.map((it) => (
                    <div
                      key={it.id}
                      className="flex items-center justify-between rounded-lg border p-2 text-sm"
                    >
                      <div className="min-w-0">
                        <div className="truncate font-medium">{it.description ?? it.job_code}</div>
                        <div className="text-xs text-muted-foreground">{it.job_code}</div>
                      </div>
                      <span className="tabular-nums whitespace-nowrap">{formatCurrency(it.total)}</span>
                    </div>
                  ))}
                </div>
                {(d.status === "awaiting_nf" || d.status === "pending_fix") && (
                  <NfUpload
                    invoiceId={d.id}
                    onDone={() => {
                      qc.invalidateQueries({ queryKey: ["portal-nerd-invoice", openId] });
                      qc.invalidateQueries({ queryKey: ["portal-nerd-invoices"] });
                      qc.invalidateQueries({ queryKey: ["portal-nerd-summary"] });
                    }}
                  />
                )}
              </div>
            )}
          </SheetBody>
        </SheetContent>
      </Sheet>
    </>
  );
}

export function PortalPage() {
  const { user } = useAuth();
  const role = user?.role;
  if (role === "nerd") return <NerdPortal />;
  if (role === "client") return <ClientPortal />;
  return (
    <PageHeader
      title="Meu painel"
      description="Este painel é exclusivo para clientes e técnicos da EuNerd."
    />
  );
}
